import { useState } from "react";
import { Body3, Circle, getTokenValue, Subtitle2, useTheme, XStack, YStack } from "@medbillai/ui";
import { money, shortDate } from "../format";
import type { ScrubReading } from "./stepPath";

/** Keeps the bubble at least this far inside the chart's edges. */
const EDGE = 10;
const ARROW = 5;
/** Keeps the bubble's arrow clear of its rounded corners. */
const ARROW_INSET = getTokenValue("$br1", "radius") + ARROW;
/** The gap from the knob up to the bubble's arrow tip. */
const LIFT = 14;
const KNOB = getTokenValue("$si1.5", "size");
export function ScrubTip({
  width,
  inset = 0,
  reading,
  beforeFirstCheck = false
}: {
  width: number;
  inset?: number;
  reading: ScrubReading;
  beforeFirstCheck?: boolean;
}) {
  const theme = useTheme();
  const [size, setSize] = useState({
    width: 0,
    height: 0
  });
  const {
    x
  } = reading;
  const edge = Math.max(EDGE, inset);
  const centre = Math.min(Math.max(x, size.width / 2 + edge), width - size.width / 2 - edge);
  const arrowX = Math.min(Math.max(size.width / 2 + x - centre, ARROW_INSET), size.width - ARROW_INSET);
  const date = shortDate(reading.point.at);
  const fill = beforeFirstCheck ? "$surface2" : "$bgBrandIntense";
  return <>
      <Circle position="absolute" left={x - KNOB / 2} top={reading.y - KNOB / 2} size="$si1.5" backgroundColor={fill} boxShadow={`0 0 0 5px ${theme.utilityOverlayInverse.val}`} pointerEvents="none" data-sentry-element="Circle" data-sentry-source-file="ScrubTip.tsx" />
      <XStack position="absolute" left={centre - size.width / 2} top={reading.y - LIFT - size.height} opacity={size.width > 0 ? 1 : 0} alignItems="baseline" gap="$sp0.75" paddingHorizontal="$sp1.5" paddingVertical="$sp0.75" borderRadius="$br1" backgroundColor={fill} pointerEvents="none" onLayout={e => setSize({
      width: e.nativeEvent.layout.width,
      height: e.nativeEvent.layout.height
    })} data-sentry-element="XStack" data-sentry-source-file="ScrubTip.tsx">
        <Subtitle2 color={beforeFirstCheck ? "$textDefault" : "$textOnBrand"} whiteSpace="nowrap" data-sentry-element="Subtitle2" data-sentry-source-file="ScrubTip.tsx">
          {beforeFirstCheck ? "No data yet" : money(reading.point.met)}
        </Subtitle2>
        <Body3 color={beforeFirstCheck ? "$textSubtle" : "$textBrandStrong"} whiteSpace="nowrap" data-sentry-element="Body3" data-sentry-source-file="ScrubTip.tsx">
          {beforeFirstCheck || reading.index === 0 ? `first check, ${date}` : `since ${date}`}
        </Body3>
        <YStack position="absolute" bottom={-ARROW} left={arrowX - ARROW} width={0} height={0} borderLeftWidth={ARROW} borderRightWidth={ARROW} borderTopWidth={ARROW} borderLeftColor="transparent" borderRightColor="transparent" borderTopColor={fill} data-sentry-element="YStack" data-sentry-source-file="ScrubTip.tsx" />
      </XStack>
    </>;
}